import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, X } from 'lucide-react';
import { PillButton } from './ui';

// #0b1a4d at 65% over the #e9ecf2 page = what the overlay looks like.
const GUTTER_TINT = 'rgb(88, 99, 135)';

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

// Confirms before sending the visitor to an external site. `target` is { title, url } or null.
const LeaveSiteDialog = ({ target, onClose }) => {
  const continueRef = useRef(null);

  useEffect(() => {
    if (!target) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    const html = document.documentElement;
    const prevOverflow = document.body.style.overflow;
    const prevBg = html.style.backgroundColor;
    document.body.style.overflow = 'hidden';
    // The reserved scrollbar gutter shows the root background, which no overlay can cover,
    // so tint it to match the overlay over the page colour.
    html.style.backgroundColor = GUTTER_TINT;
    window.addEventListener('keydown', onKey);
    const focus = setTimeout(() => continueRef.current?.querySelector('button')?.focus(), 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      html.style.backgroundColor = prevBg;
      window.removeEventListener('keydown', onKey);
      clearTimeout(focus);
    };
  }, [target, onClose]);

  const proceed = () => {
    window.open(target.url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {target && (
        <motion.div
          key="leave-site"
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Solid tint, no backdrop blur: blurring the animated page behind caused dropped frames */}
          <div className="absolute inset-0 bg-[#0b1a4d]/65" onClick={onClose} />

          {/* Only opacity + translate (GPU-friendly); scaling made the text shimmer while it settled */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="leave-site-title"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ willChange: 'transform, opacity' }}
            className="relative w-full max-w-[440px] rounded-3xl bg-[#f4f6fa] p-6 sm:p-7 shadow-[0_30px_80px_rgba(0,0,0,0.3)]"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 w-9 h-9 rounded-full flex items-center justify-center text-[#111]/60 hover:bg-[#e6e9f0] hover:text-[#111] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-white bg-[radial-gradient(120%_120%_at_25%_10%,#6f86ff_0%,#3b5bfd_55%,#1f33c9_100%)] shadow-[0_10px_20px_rgba(59,91,253,0.3)]">
              <ExternalLink className="w-5 h-5" />
            </span>

            <h3 id="leave-site-title" className="mt-5 text-[22px] font-medium tracking-[-0.03em] text-[#111]">
              You're leaving this website
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-[#111]/65">
              <span className="text-[#111] font-medium">{target.title}</span>{' '}
              {target.kind === 'app' ? 'is live on Google Play.' : 'is a live client website.'} It will open in a
              new tab, so you can come back here anytime.
            </p>

            <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#e6e9f0] px-3.5 py-2.5 text-sm text-[#111]/80">
              <ExternalLink className="w-3.5 h-3.5 shrink-0 text-[#3b5bfd]" />
              <span className="truncate">{hostOf(target.url)}</span>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-6 rounded-full text-sm font-medium text-[#111] bg-white hover:bg-[#e6e9f0] transition-colors"
              >
                Stay here
              </button>
              <div ref={continueRef}>
                <PillButton type="button" onClick={proceed}>
                  Continue
                </PillButton>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LeaveSiteDialog;
